import { Suspense, useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchPledge } from 'api/pledges';
import { donate } from 'api/transactions';
import {
    TransactionFundInput,
    TransactionsFormValues,
} from 'assets/js/pages/transactions/TransactionForm';
import * as CustomFieldResponseForm from 'components/custom-fields/CustomFieldResponseForm';
import {
    CustomFieldFormValue,
    toFormValues,
    toRequestValues,
} from 'components/custom-fields/helpers';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { SelectItemWithLabel, SelectListItem, UseFormSearchableSelect } from 'components/select';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { useSelectedCampaignCustomFields } from 'customHooks/useSelectedCampaignCustomFields';
import { maskedDateToServerDateTime } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Campaign, TransactionDonation, TransactionMethod } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface PledgeTransactionForm {
    amount: string;
    checkNumber: string;
    paymentMethod: TransactionMethod;
    donationDate: string;
    depositDate: string;
    message: string;
    internalNote: string;
    hideName: boolean;
    hideAmount: boolean;
    sendReceipt: boolean;
    selectedFund: string[];
    selectedCampaign: SelectItemWithLabel<Campaign>[];
    customFields: {
        [field: string]: CustomFieldFormValue;
    };
}

const PledgeTransactionDrawer = () => {
    const toast = useToast();
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const pledgeId = parseInt(pledge_id);
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const [isAddingTransactionOfflineFile, setIsAddingTransactionOfflineFile] = useState(false);
    const [searchCampaigns, setSearchCampaigns] = useState('');
    const debouncedSearchCampaigns = useDebounce(searchCampaigns);
    const history = useHistory();

    const { data: pledge } = useSuspenseQuery({
        queryKey: accountQueryKeys.pledgeById(accountId, pledgeId),
        queryFn: () => {
            return fetchPledge(accountId, pledgeId);
        },
    });

    const { data: campaignsData, ref: campaignsRef } = useInfiniteCampaigns({
        query: debouncedSearchCampaigns,
    });
    const campaigns = campaignsData?.pages ?? [];

    const methods = useGivebutterForm<PledgeTransactionForm>({
        defaultValues: {
            amount: '',
            checkNumber: '',
            paymentMethod: 'cash',
            donationDate: DateTime.now().toFormat('MM/dd/yyyy'),
            depositDate: '',
            hideName: false,
            hideAmount: false,
            sendReceipt: false,
            message: '',
            selectedFund: pledge.fund?.id ? [pledge.fund.id.toString()] : [],
            selectedCampaign: pledge.campaign.id
                ? [
                      {
                          id: pledge.campaign.id.toString(),
                          label: pledge.campaign.title,
                          value: pledge.campaign,
                      },
                  ]
                : [],
            customFields: {},
        },
    });
    const { setError, path, control, resetFormButton, handleSubmit, watch, setValue } = methods;

    const [paymentMethod, rawSelectedCampaign, rawSelectedFund, customFieldsRaw] = watch([
        'paymentMethod',
        'selectedCampaign',
        'selectedFund',
        'customFields',
    ]);

    const selectedCampaign = rawSelectedCampaign?.[0];
    const selectedFund = rawSelectedFund?.[0];

    const { customFields, selectCampaign } = useSelectedCampaignCustomFields({
        onSuccess: customFields => {
            setValue('customFields', toFormValues(customFields));
        },
    });

    const { mutate: submit, isPending: isSubmitting } = useMutation({
        mutationFn: (formValues: PledgeTransactionForm) => {
            const campaignId = selectedCampaign?.id ? parseInt(selectedCampaign.id) : null;
            const contactId = pledge.contact?.id || null;
            const fundId = selectedFund ? parseInt(selectedFund) : null;

            return donate({
                hide_name: formValues.hideName,
                hide_amount: formValues.hideAmount,
                payment_method: formValues.paymentMethod,
                check_number: formValues.checkNumber ?? '',
                deposited_at:
                    formValues.depositDate.length > 0
                        ? maskedDateToServerDateTime(formValues.depositDate)
                        : '',
                transacted_at:
                    formValues.donationDate.length > 0
                        ? maskedDateToServerDateTime(formValues.donationDate)
                        : '',
                amount: formValues.amount,
                send_confirmation_email: formValues.sendReceipt,
                message: formValues.message,
                fund: fundId,
                campaign_id: campaignId,
                contact_id: contactId,
                pledge: {
                    id: pledgeId,
                },
                custom_fields: customFields
                    ? toRequestValues(customFieldsRaw, customFields, { useId: true })
                    : null,
            });
        },
        onSuccess: () => {
            toast.success('Transaction created successfully!');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
                type: 'all',
            });

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledges(accountId),
                type: 'all',
            });

            history.replace(`/accounts/${accountId}/pledges/${pledgeId}/edit`, {
                activeTab: 'payments',
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? `An unknown error occurred.`);
            resetFormButton();

            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    const message = errors[key][0];

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<PledgeTransactionForm>, {
                            message,
                        });
                    }
                });
            }
        },
    });

    const hasCustomFields =
        !!selectedCampaign &&
        !!customFields &&
        customFields.length > 0 &&
        Object.keys(customFieldsRaw).length > 0;

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<PledgeTransactionForm>)}
            >
                <Drawer.Body className="flex flex-col gap-6">
                    <p className="flex items-center gap-4">
                        Pledge details{' '}
                        <FontAwesomeIcon className="text-gray-400" icon={faAngleRight} /> New
                        transaction
                    </p>
                    <Form.Container>
                        <Form.Label name={path('selectedCampaign')}>
                            Campaign
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <UseFormSearchableSelect
                            name={path('selectedCampaign')}
                            control={control}
                            multi={false}
                            localSearch={false}
                            clearable={true}
                            rules={{
                                required: 'Select a campaign',
                            }}
                            inputProps={{
                                placeholder: 'Select a campaign',
                                onChange: e => {
                                    setSearchCampaigns(e.currentTarget.value);
                                },
                            }}
                            onSelectItem={value => {
                                if (!value) {
                                    setSearchCampaigns('');
                                    setValue('selectedFund', []);
                                    setValue('customFields', {});
                                    return;
                                }

                                selectCampaign(parseInt(value.id));
                            }}
                        >
                            {campaigns.map((item: Campaign) => (
                                <SelectListItem
                                    key={item.id}
                                    value={{
                                        id: item.id.toString(),
                                        label: item.title,
                                        value: item,
                                    }}
                                >
                                    {item.title}
                                </SelectListItem>
                            ))}
                            <div ref={campaignsRef} />
                        </UseFormSearchableSelect>
                        <Form.Error name={path('selectedCampaign')} />
                    </Form.Container>

                    {selectedCampaign?.value?.has_funds && (
                        <Suspense fallback={null}>
                            <Form.Container>
                                <Form.Label name={path('selectedFund')}>Fund</Form.Label>
                                <TransactionFundInput />
                            </Form.Container>
                        </Suspense>
                    )}
                    {pledge.contact && (
                        <Form.Container>
                            <p className="font-medium">Supporter</p>
                            <Link to={`/accounts/${accountId}/contacts/${pledge.contact.id}`}>
                                {pledge.contact.first_name} {pledge.contact.last_name}
                            </Link>
                        </Form.Container>
                    )}
                    <Form.Container>
                        <Form.Label name={path('amount')}>
                            Amount
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={'amount'}
                            rules={{
                                required: 'Amount is required',
                            }}
                            inputRootProps={{
                                mask: 'decimal',
                            }}
                        >
                            <InputCurrencyIcon />
                            <Form.TextInputForm placeholder={'0.00'} />
                        </Form.TextInputRoot>
                        {['in-kind', 'stock', 'property', 'other'].includes(paymentMethod) && (
                            <p className={'text-sm text-gray-600'}>
                                This signifies the estimated value of the gift. Non-monetary
                                contributions may have a $0.00 value.
                            </p>
                        )}
                        <Form.Error name={path('amount')} />
                    </Form.Container>
                    <div className="flex flex-row gap-4">
                        <Form.Container>
                            <Form.Label name={path('donationDate')}>
                                Donation date
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={'donationDate'}
                                rules={{
                                    required: 'Donation date is required',
                                }}
                                inputRootProps={{
                                    mask: 'date',
                                }}
                            >
                                <Form.TextInputForm placeholder="MM/DD/YYYY" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('donationDate')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('paymentMethod')}>
                                Payment method
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.SingleSelect control={control} name="paymentMethod">
                                <SingleSelect.Trigger aria-label="Payment method" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="cash">
                                        <SingleSelect.ItemText>Cash</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="check">
                                        <SingleSelect.ItemText>Check</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="in-kind">
                                        <SingleSelect.ItemText>In-kind</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="stock">
                                        <SingleSelect.ItemText>Stock</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="property">
                                        <SingleSelect.ItemText>Property</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="other">
                                        <SingleSelect.ItemText>Other</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </Form.Container>
                    </div>
                    {paymentMethod === 'check' && (
                        <div className="flex flex-row gap-4">
                            <Form.Container>
                                <Form.Label name={path('depositDate')}>Deposit date</Form.Label>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'depositDate'}
                                    inputRootProps={{
                                        mask: 'date',
                                    }}
                                >
                                    <Form.TextInputForm placeholder="MM/DD/YYYY" />
                                </Form.TextInputRoot>
                                <Form.Error name={path('depositDate')} />
                            </Form.Container>
                            <Form.Container>
                                <Form.Label name={path('checkNumber')}>Check number</Form.Label>
                                <Form.TextInputRoot control={control} name={'checkNumber'}>
                                    <Form.TextInputForm placeholder={'Check number'} />
                                </Form.TextInputRoot>
                                <Form.Error name={path('checkNumber')} />
                            </Form.Container>
                        </div>
                    )}
                    <Form.Container>
                        <Form.Label name={path('message')}>Message</Form.Label>
                        <Form.Textarea
                            control={control}
                            name={'message'}
                            placeholder={'Type message'}
                            rows={8}
                        />
                        <p className={'text-sm text-gray-600'}>
                            Message will appear in the supporter feed if the campaign is active.
                        </p>
                    </Form.Container>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('sendReceipt')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('sendReceipt')} className={'leading-none'}>
                                Send a receipt
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                After saving, a receipt will be sent to the contact’s email address.
                            </p>
                        </div>
                    </Form.Container>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('hideName')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('hideName')} className={'leading-none'}>
                                Do not display name publicly
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                When enabled, the contact’s name will be hidden on the public-facing
                                campaign page.
                            </p>
                        </div>
                    </Form.Container>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('hideAmount')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('hideAmount')} className={'leading-none'}>
                                Do not display amount publicly
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                The amount donated will be hidden on the public-facing campaign
                                page.
                            </p>
                        </div>
                    </Form.Container>
                    {hasCustomFields && (
                        <>
                            <Separator className={'-mx-8 !w-auto'} />
                            <div className={'flex flex-col'}>
                                <p className={'font-medium'}>Custom fields</p>
                                <p className={'text-sm text-gray-600'}>
                                    The campaign you selected has custom fields.
                                </p>
                            </div>
                            {customFields.map(field => (
                                <CustomFieldResponseForm.Root
                                    key={field.id}
                                    field={field}
                                    onAddFile={setIsAddingTransactionOfflineFile}
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-col">
                                            <CustomFieldResponseForm.Title />
                                            <CustomFieldResponseForm.Description />
                                        </div>

                                        <div className="flex flex-col">
                                            <CustomFieldResponseForm.Input />
                                            <CustomFieldResponseForm.FormError />
                                        </div>
                                    </div>
                                </CustomFieldResponseForm.Root>
                            ))}
                        </>
                    )}
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel
                            asChild={true}
                            onClick={e => {
                                e.preventDefault();
                                history.replace(`/accounts/${accountId}/pledges/${pledgeId}/edit`);
                            }}
                        >
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton
                                isSubmitting={isSubmitting || isAddingTransactionOfflineFile}
                            >
                                Add transaction
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const transformServerKeyIntoFormKey = (
    serverKey: keyof TransactionDonation | 'campaign',
): keyof PledgeTransactionForm | null => {
    switch (serverKey) {
        case 'payment_method':
            return 'paymentMethod';
        case 'deposited_at':
            return 'depositDate';
        case 'check_number':
            return 'checkNumber';
        case 'hide_amount':
            return 'hideAmount';
        case 'hide_name':
            return 'hideName';
        case 'send_confirmation_email':
            return 'sendReceipt';
        case 'transacted_at':
            return 'donationDate';
        case 'campaign':
        case 'campaign_id':
            return 'selectedCampaign';
        case 'fund':
            return 'selectedFund';
        case 'amount':
        case 'message':
            return serverKey;
        default:
            return null;
    }
};

export { PledgeTransactionDrawer };
export type { TransactionsFormValues };
